import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Stack,
  TextField,
  Chip,
  alpha,
  useTheme,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  Close,
  ShoppingCart,
  Add,
  Remove,
  Restaurant,
  PersonOutlined,
  PhoneOutlined,
  LocalOfferOutlined,
  NoteAltOutlined,
  CheckCircle,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import OrderService from "../../../services/OrderService";
import CompanyServices from "../../../services/CompanyService";
import CompanyConfigurationServices from "../../../services/CompanyConfigurationService";
import DiscountService from "../../../services/DiscountService";
import PaymentService from "../../../services/PaymentService";
import ImageLoader from "components/ImageLoader/ImageLoader";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import { useUserWithCompany } from "../../../hooks/useCompany";
import OrderSuccessDialog from "./OrderSuccessDialog";
import { formatCurrency } from "../../../utils/currencyUtils";
import { isValidPhoneNumber, sanitizePhoneNumber } from "../../../utils/phoneUtils";
import { LUXURY, getMenuCartChrome } from "data/guestLuxuryTheme";

const CartDrawer = ({
  open,
  onClose,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  luxury = false,
  initialTableId,
  initialOrderType,
  companyId: propCompanyId,
  initialCustomer,
  guestMode,
  locationLabel,
  onOrderPlaced,
}) => {
  const theme = useTheme();
  const {
    companyId: hookCompanyId,
    vatRate,
    serviceChargeRateDining,
    serviceChargeRateTakeaway,
    currency,
    company,
  } = useUserWithCompany();
  const companyId = propCompanyId || hookCompanyId;

  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [orderType, setOrderType] = useState(initialOrderType || (initialTableId ? "dine-in" : "takeaway"));
  const [tableId, setTableId] = useState(initialTableId || "");
  const isQRMode = !!initialTableId;
  const [discountCode, setDiscountCode] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [dineInPayChoice, setDineInPayChoice] = useState("later");
  const [loading, setLoading] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [orderTypeConfig, setOrderTypeConfig] = useState({
    Dinein: true,
    Takeaway: true,
    Delivery: true,
  });
  const fetchingRef = React.useRef(false);

  // Discount state
  const [availableDiscounts, setAvailableDiscounts] = useState([]);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState("");
  const isCustomerMobileInvalid =
    customerMobile.trim().length > 0 && !isValidPhoneNumber(customerMobile);
  const detailsReady = isValidPhoneNumber(sanitizePhoneNumber(customerMobile));

  React.useEffect(() => {
    if (!open || !initialCustomer) return;

    if (!customerMobile && initialCustomer.mobile) {
      setCustomerMobile(initialCustomer.mobile);
    }

    if (!customerName && initialCustomer.name) {
      setCustomerName(initialCustomer.name);
    }
  }, [open, initialCustomer, customerMobile, customerName]);

  // Fetch available discounts when drawer opens
  React.useEffect(() => {
    if (!open || !companyId) return;
    DiscountService.getAll({ companyId, page: 0, limit: 50 })
      .then((res) => {
        const discounts = res?.data?.view?.data || [];
        const today = new Date();
        const active = discounts.filter(
          (d) =>
            d.isActive &&
            new Date(d.startDate) <= today &&
            new Date(d.endDate) >= today,
        );
        setAvailableDiscounts(active);
      })
      .catch(() => { });
  }, [open, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!open || !companyId) return;

    CompanyConfigurationServices.getConfigurations(companyId, "order_settings")
      .then((response) => {
        if (!response?.success) return;

        const items =
          response?.data?.view?.data ??
          response?.data?.view?.items ??
          response?.data?.items ??
          [];

        if (items.length > 0) {
          const value = items[0]?.value || {};
          setOrderTypeConfig({
            Dinein: Boolean(value.Dinein),
            Takeaway: Boolean(value.Takeaway),
            Delivery: Boolean(value.Delivery),
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching order type configuration:", error);
      });
  }, [open, companyId]);

  // Fetch company data once when drawer opens, only if rates are missing
  React.useEffect(() => {
    if (!open || !companyId || companyData || fetchingRef.current) return;
    const needsFetch =
      !company ||
      (vatRate === 0 &&
        serviceChargeRateDining === 0 &&
        serviceChargeRateTakeaway === 0);
    if (!needsFetch) return;

    fetchingRef.current = true;
    CompanyServices.getById(companyId)
      .then((response) => {
        if (response?.data?.view) {
          setCompanyData(response.data.view);
        }
      })
      .catch((error) => {
        console.error("Error fetching company data:", error);
      })
      .finally(() => {
        fetchingRef.current = false;
      });
  }, [open, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use fetched company data or fallback to Redux data
  const activeVatRate = companyData?.vatRate
    ? parseFloat(companyData.vatRate)
    : vatRate;
  const activeServiceChargeRateDining = companyData?.serviceChargeRateDining
    ? parseFloat(companyData.serviceChargeRateDining)
    : serviceChargeRateDining;
  const activeServiceChargeRateTakeaway = companyData?.serviceChargeRateTakeaway
    ? parseFloat(companyData.serviceChargeRateTakeaway)
    : serviceChargeRateTakeaway;
  const activeCurrency = companyData?.currency || currency || "Rs.";
  // Keep CartDrawer button/title colors exactly aligned with MenuItemCard add button
  const chrome = getMenuCartChrome(luxury);
  const CART_ACCENT = chrome.accent;
  const CART_BTN_GRADIENT = chrome.gradient;
  const CART_BTN_GRADIENT_HOVER = chrome.gradientHover;
  const CART_BTN_SHADOW = chrome.shadow;
  const CART_BTN_SHADOW_HOVER = chrome.shadowHover;

  // Helper function to extract actual values from nested structure
  const getItemData = (item) => {
    const actualMenuItem = item.menuItem?.menuItem || item.menuItem;
    const actualVariation = item.menuItem?.variation || item.variation;
    const actualModifiers = item.modifiers || item.menuItem?.modifiers || [];

    return { actualMenuItem, actualVariation, actualModifiers };
  };

  // Calculate subtotal
  const subtotal = cartItems.reduce((total, item) => {
    const { actualMenuItem, actualVariation, actualModifiers } =
      getItemData(item);

    // Get base price with comprehensive fallback
    let basePrice = 0;

    if (
      actualVariation &&
      actualVariation.price !== undefined &&
      actualVariation.price !== null &&
      actualVariation.price !== ""
    ) {
      const varPrice = String(actualVariation.price).replace(/[^0-9.-]/g, "");
      basePrice = parseFloat(varPrice) || 0;
    } else if (
      actualMenuItem?.basePrice !== undefined &&
      actualMenuItem?.basePrice !== null &&
      actualMenuItem?.basePrice !== ""
    ) {
      const bPrice = String(actualMenuItem.basePrice).replace(/[^0-9.-]/g, "");
      basePrice = parseFloat(bPrice) || 0;
    } else if (
      actualMenuItem?.price !== undefined &&
      actualMenuItem?.price !== null &&
      actualMenuItem?.price !== ""
    ) {
      const itemPrice = String(actualMenuItem.price).replace(/[^0-9.-]/g, "");
      basePrice = parseFloat(itemPrice) || 0;
    }

    // Calculate modifiers price
    const modifiersPrice = actualModifiers.reduce((sum, mod) => {
      const modPriceValue = mod?.price || "0";
      const modPrice =
        parseFloat(String(modPriceValue).replace(/[^0-9.-]/g, "")) || 0;
      return sum + modPrice;
    }, 0);

    const itemPrice = basePrice + modifiersPrice;
    return total + itemPrice * item.quantity;
  }, 0);

  // Calculate service charge & VAT (same formula as calculateOrderTotals in cartUtils)
  const serviceChargeRate =
    orderType === "dine-in"
      ? activeServiceChargeRateDining
      : activeServiceChargeRateTakeaway;
  const serviceCharge = (subtotal * serviceChargeRate) / 100;
  const subtotalWithServiceCharge = subtotal + serviceCharge;
  const vatAmount = (subtotalWithServiceCharge * activeVatRate) / 100;
  const grandTotalBeforeDiscount = subtotalWithServiceCharge + vatAmount;

  // Calculate discount amount
  const calcDiscountAmount = (discount, base) => {
    if (!discount) return 0;
    const value = parseFloat(discount.value) || 0;
    const max = parseFloat(discount.maxDiscountAmount) || Infinity;
    const raw =
      discount.type === "percentage" ? (base * value) / 100 : value;
    return Math.min(raw, max, base);
  };
  const discountAmount = calcDiscountAmount(appliedDiscount, grandTotalBeforeDiscount);
  const grandTotal = Math.max(0, grandTotalBeforeDiscount - discountAmount);

  const handleApplyDiscount = (discount) => {
    setDiscountError("");
    const min = parseFloat(discount.minOrderAmount) || 0;
    if (subtotal < min) {
      setDiscountError(
        `Minimum order of ${formatCurrency(min, activeCurrency)} required for "${discount.name}"`
      );
      return;
    }
    setAppliedDiscount(discount);
    setDiscountCode(discount.code);
    toast.success(`Discount "${discount.name}" applied!`);
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode("");
    setDiscountError("");
  };

  const handlePlaceOrder = async () => {
    const normalizedCustomerMobile = sanitizePhoneNumber(customerMobile);

    // Validation
    if (!normalizedCustomerMobile) {
      toast.error("Please provide your contact number");
      return;
    }
    if (!isValidPhoneNumber(normalizedCustomerMobile)) {
      toast.error("Please enter a valid contact number (10 to 15 digits)");
      return;
    }
    if (cartItems.length === 0) {
      toast.error("Please add items to your order");
      return;
    }
    if (!orderType) {
      toast.error("No order type is enabled for this restaurant");
      return;
    }

    try {
      setLoading(true);

      // Prepare order items
      const items = cartItems.map((item) => {
        const orderItem = {
          menuItemId: item.menuItem.id,
          quantity: item.quantity,
        };

        if (item.variation) {
          orderItem.variationId = item.variation.id;
        }

        if (item.modifiers && item.modifiers.length > 0) {
          orderItem.modifiers = item.modifiers.map((mod) => mod.id);
        }

        if (item.specialInstructions) {
          orderItem.specialInstructions = item.specialInstructions;
        }

        return orderItem;
      });

      // Prepare order data
      const orderData = {
        customerName: customerName.trim() || "Guest",
        customerMobile: normalizedCustomerMobile,
        orderType,
        items,
        checkStock: true,
        companyId,
      };

      // Add optional fields
      if (tableId) {
        orderData.tableId = tableId;
      }
      if (discountCode) {
        orderData.discountCode = discountCode;
      }
      const instructionPrefix =
        guestMode === "room-service" && locationLabel
          ? `Deliver to ${locationLabel}.`
          : "";
      const combinedNotes = [instructionPrefix, specialInstructions.trim()]
        .filter(Boolean)
        .join(" ");
      if (combinedNotes) {
        orderData.specialInstructions = combinedNotes;
      }

      const response = await OrderService.createOrder(orderData);

      if (response.status === 200 || response.status === 201) {
        toast.success("Order placed successfully!");

        const placedOrder = response.data.inserted;

        // Create payment record for takeaway/delivery, or dine-in if user chose "Pay Now"
        const shouldPay =
          orderType === "takeaway" ||
          orderType === "delivery" ||
          (orderType === "dine-in" && dineInPayChoice === "now");

        if (shouldPay) {
          try {
            const paymentAmount = parseFloat(placedOrder.totalAmount || 0);
            await PaymentService.create({
              companyId,
              orderId: placedOrder.id,
              amount: paymentAmount,
              paymentMethod: "cash",
              paymentStatus: "completed",
              transactionId: "TXN123456",
              notes: "Payment received",
              status: "completed",
            });
          } catch (paymentError) {
            console.error("Payment record creation failed:", paymentError);
          }
        }

        setOrderResult(placedOrder);
        setShowSuccessDialog(true);
        onOrderPlaced?.(placedOrder);
      } else {
        toast.error(response.message || "Failed to place order");
      }
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error("Failed to place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    setOrderResult(null);
    onClearCart();
    setCustomerName("");
    setCustomerMobile("");
    setTableId(initialTableId || "");
    setDiscountCode("");
    setAppliedDiscount(null);
    setSpecialInstructions("");
    onClose();
  };

  const ORDER_TYPES = [
    ...(isQRMode
      ? [{
          value: "dine-in",
          label: guestMode === "room-service" ? "Room dining" : "Dine In",
          emoji: guestMode === "room-service" ? "🛎️" : "🍽️",
        }]
      : []),
    ...(!isQRMode ? [{ value: "takeaway", label: "Takeaway", emoji: "🥡" }] : []),
    ...(!isQRMode ? [{ value: "delivery", label: "Delivery", emoji: "🚚" }] : []),
  ].filter((opt) => {
    if (opt.value === "dine-in") return orderTypeConfig.Dinein;
    if (opt.value === "takeaway") return orderTypeConfig.Takeaway;
    if (opt.value === "delivery") return orderTypeConfig.Delivery;
    return true;
  });

  React.useEffect(() => {
    if (ORDER_TYPES.length === 0) {
      if (orderType !== "") setOrderType("");
      return;
    }

    const isCurrentAllowed = ORDER_TYPES.some((opt) => opt.value === orderType);
    if (!isCurrentAllowed) {
      setOrderType(ORDER_TYPES[0].value);
    }
  }, [orderType, ORDER_TYPES]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 2,
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: "100%", md: 920, lg: 980 },
            height: "100%",
            maxHeight: "100dvh",
            overflow: "hidden",
            background: luxury ? LUXURY.page : "#f0f2fa",
            boxShadow: "-12px 0 48px rgba(0,0,0,0.16)",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* ── HEADER ── */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: luxury ? LUXURY.barGradient : CART_BTN_GRADIENT,
            flexShrink: 0,
            boxShadow: CART_BTN_SHADOW,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2.5,
                bgcolor: alpha("#ffffff", 0.18),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${alpha("#ffffff", 0.22)}`,
              }}
            >
              <ShoppingCart sx={{ fontSize: 22, color: "#ffffff" }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1, color: "#ffffff", letterSpacing: 0.2 }}>
                Your Order
              </Typography>
              <Typography variant="caption" sx={{ color: alpha("#ffffff", 0.75), fontWeight: 500 }}>
                {cartItems.length} {cartItems.length !== 1 ? "items" : "item"} in cart
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            {cartItems.length > 0 && (
              <Tooltip title="Clear cart">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (window.confirm("Clear all items from your cart?"))
                      onClearCart();
                  }}
                  sx={{
                    color: alpha("#ffffff", 0.75),
                    "&:hover": { bgcolor: alpha("#ffffff", 0.12), color: "#ffffff" },
                  }}
                >
                  <RemoveShoppingCartIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: alpha("#ffffff", 0.75),
                "&:hover": { bgcolor: alpha("#ffffff", 0.12), color: "#ffffff" },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* ── BODY ── */}
        {cartItems.length === 0 ? (
          /* ── EMPTY STATE ── */
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                bgcolor: alpha(CART_ACCENT, 0.06),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShoppingCart
                sx={{
                  fontSize: 48,
                  color: alpha(CART_ACCENT, 0.3),
                }}
              />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Your cart is empty
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ maxWidth: 260 }}
            >
              Browse our menu and add something delicious to get started!
            </Typography>
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                mt: 1,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                px: 4,
                background: CART_BTN_GRADIENT,
                boxShadow: CART_BTN_SHADOW,
                "&:hover": {
                  background: CART_BTN_GRADIENT_HOVER,
                  boxShadow: CART_BTN_SHADOW_HOVER,
                },
              }}
            >
              Explore Menu
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                pb: { xs: 1, md: 0 },
              }}
            >
            {/* ── LEFT PANEL: Order Type + Cart Items ── */}
            <Box
              sx={{
                flex: { xs: "0 0 auto", md: "1 1 55%" },
                display: "flex",
                flexDirection: "column",
                bgcolor: luxury ? LUXURY.page : "#f0f2fa",
                borderRight: {
                  md: `1px solid ${alpha(CART_ACCENT, 0.15)}`,
                },
              }}
            >
              {/* Order Type Selector */}
              <Box
                sx={{
                  px: 2.5,
                  pt: 2.5,
                  pb: 2,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  bgcolor: "#ffffff",
                  flexShrink: 0,
                }}
              >
                <Box sx={{ mb: 1.5 }}>
                  {isQRMode && (
                    <Chip
                      label={locationLabel || (guestMode === "room-service" ? "Room dining" : "Table QR")}
                      size="small"
                      sx={{
                        mb: 1,
                        height: 26,
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        bgcolor: alpha(CART_ACCENT, 0.1),
                        color: CART_ACCENT,
                        border: `1px solid ${alpha(CART_ACCENT, 0.22)}`,
                      }}
                    />
                  )}
                  <Typography sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", letterSpacing: 1, lineHeight: 1.4 }}>
                    How would you like to receive your order?
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {ORDER_TYPES.map((opt) => {
                    const active = orderType === opt.value;
                    return (
                      <Box
                        key={opt.value}
                        onClick={() => !isQRMode && setOrderType(opt.value)}
                        sx={{
                          flex: 1,
                          py: 1.5,
                          px: 1,
                          borderRadius: 3,
                          border: `2px solid ${active ? CART_ACCENT : alpha(theme.palette.divider, 0.3)}`,
                          bgcolor: active ? alpha(CART_ACCENT, 0.08) : "#f5f6fa",
                          cursor: isQRMode ? "default" : "pointer",
                          transition: "all 0.18s ease",
                          textAlign: "center",
                          userSelect: "none",
                          opacity: isQRMode && !active ? 0.4 : 1,
                          boxShadow: active ? `0 4px 14px ${alpha(CART_ACCENT, 0.22)}` : "none",
                          ...(!isQRMode && { "&:hover": { borderColor: CART_ACCENT, bgcolor: alpha(CART_ACCENT, 0.05) } }),
                        }}
                      >
                        <Typography sx={{ fontSize: "1.5rem", lineHeight: 1.2, mb: 0.5 }}>{opt.emoji}</Typography>
                        <Typography sx={{ fontWeight: active ? 800 : 500, fontSize: "0.75rem", color: active ? CART_ACCENT : "#64748b", lineHeight: 1 }}>
                          {opt.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                {ORDER_TYPES.length === 0 && (
                  <Typography
                    sx={{
                      mt: 1.25,
                      fontSize: "0.82rem",
                      color: theme.palette.error.main,
                      fontWeight: 600,
                    }}
                  >
                    No order type is enabled for this restaurant. Please contact staff.
                  </Typography>
                )}
              </Box>

              {/* Items List */}
              <Box sx={{
                p: 2.5,
                backgroundImage: "linear-gradient(rgba(255,255,255,0.72), rgba(255,255,255,0.72)), url('/assets/images/BackdropMenu.png')",
                backgroundRepeat: "repeat",
                backgroundSize: "180px auto",
                backgroundPosition: "top left",
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.secondary,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    mb: 2,
                    display: "block",
                  }}
                >
                  Your Selections ({cartItems.length})
                </Typography>
                <Stack spacing={1.5}>
                  {cartItems.map((item, index) => {
                    const { actualMenuItem, actualVariation, actualModifiers } =
                      getItemData(item);

                    // Calculate unit price with comprehensive fallback
                    let basePrice = 0;

                    if (
                      actualVariation &&
                      actualVariation.price !== undefined &&
                      actualVariation.price !== null &&
                      actualVariation.price !== ""
                    ) {
                      // If variation exists, use its price
                      const varPrice = String(actualVariation.price).replace(
                        /[^0-9.-]/g,
                        "",
                      );
                      basePrice = parseFloat(varPrice) || 0;
                    } else if (
                      actualMenuItem?.basePrice !== undefined &&
                      actualMenuItem?.basePrice !== null &&
                      actualMenuItem?.basePrice !== ""
                    ) {
                      // Use basePrice (the configured price for no-variation items)
                      const bPrice = String(actualMenuItem.basePrice).replace(
                        /[^0-9.-]/g,
                        "",
                      );
                      basePrice = parseFloat(bPrice) || 0;
                    } else if (
                      actualMenuItem?.price !== undefined &&
                      actualMenuItem?.price !== null &&
                      actualMenuItem?.price !== ""
                    ) {
                      // Fallback to price field
                      const itemPrice = String(actualMenuItem.price).replace(
                        /[^0-9.-]/g,
                        "",
                      );
                      basePrice = parseFloat(itemPrice) || 0;
                    } else {
                      console.warn(
                        "No price found for item:",
                        actualMenuItem?.name,
                      );
                    }

                    const modifiersTotal = actualModifiers.reduce(
                      (sum, mod) => {
                        const modPriceValue = mod?.price || "0";
                        const modPrice =
                          parseFloat(
                            String(modPriceValue).replace(/[^0-9.-]/g, ""),
                          ) || 0;
                        return sum + modPrice;
                      },
                      0,
                    );

                    const unitPrice = basePrice + modifiersTotal;

                    return (
                      <Box
                        key={index}
                        sx={{
                          bgcolor: "#ffffff",
                          borderRadius: "14px",
                          border: "1px solid #e8eaf5",
                          boxShadow: `0 2px 10px ${alpha(CART_ACCENT, 0.08)}`,
                          overflow: "visible",
                          transition: "all 0.22s ease",
                          "&:hover": {
                            boxShadow: `0 8px 28px ${alpha(CART_ACCENT, 0.18)}`,
                            borderColor: "#d4d9f5",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <Box sx={{ p: 1.5, display: "flex", gap: 1.5 }}>
                          {/* Image */}
                          <Box sx={{ position: "relative", flexShrink: 0 }}>
                            <Box
                              sx={{
                                width: 84,
                                height: 84,
                                borderRadius: 3,
                                overflow: "hidden",
                                bgcolor: "#f1f5f9",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
                              }}
                            >
                              {actualMenuItem?.image ? (
                                <ImageLoader
                                  imagePath={actualMenuItem.image}
                                  width="100%"
                                  height="100%"
                                  alt={actualMenuItem?.name || "Menu item"}
                                  rounded={false}
                                  borderRadius={0}
                                />
                              ) : (
                                <Restaurant sx={{ fontSize: 34, color: alpha(CART_ACCENT, 0.25) }} />
                              )}
                            </Box>
                          </Box>

                          {/* Details */}
                          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {/* Name + X */}
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.5 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#0f172a", lineHeight: 1.35, flex: 1, minWidth: 0, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                                {actualMenuItem?.name}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => onRemoveItem(index)}
                                sx={{
                                  p: 0.35,
                                  mt: -0.25,
                                  ml: 0.5,
                                  color: "#cbd5e1",
                                  flexShrink: 0,
                                  "&:hover": { color: theme.palette.error.main, bgcolor: alpha(theme.palette.error.main, 0.08) },
                                  transition: "all 0.18s",
                                }}
                              >
                                <Close sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Box>

                            {/* Variation + modifier chips */}
                            {(actualVariation || actualModifiers.length > 0) && (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {actualVariation && (
                                  <Chip label={actualVariation.name} size="small" sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700, bgcolor: alpha(CART_ACCENT, 0.1), color: CART_ACCENT, border: "none", borderRadius: 1 }} />
                                )}
                                {actualModifiers.map((mod, idx) => (
                                  <Chip
                                    key={idx}
                                    label={`${mod.name}${parseFloat(mod.price || 0) > 0 ? ` +${formatCurrency(mod.price, activeCurrency)}` : ""}`}
                                    size="small"
                                    sx={{ height: 18, fontSize: "0.6rem", fontWeight: 600, bgcolor: "#f1f5f9", color: "#64748b", border: "none", borderRadius: 1 }}
                                  />
                                ))}
                              </Box>
                            )}

                            {/* Stepper + price */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "auto", pt: 0.5 }}>
                              {/* Pill stepper */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  bgcolor: "#eef0fa",
                                  borderRadius: 10,
                                  border: `1.5px solid ${alpha(CART_ACCENT, 0.2)}`,
                                  p: 0.4,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => onUpdateQuantity(index, item.quantity - 1)}
                                  disabled={item.quantity <= 1}
                                  sx={{
                                    width: 26, height: 26, p: 0,
                                    bgcolor: item.quantity > 1 ? alpha(CART_ACCENT, 0.14) : "transparent",
                                    color: item.quantity > 1 ? CART_ACCENT : "#cbd5e1",
                                    borderRadius: "50%",
                                    "&:hover": { bgcolor: alpha(CART_ACCENT, 0.24) },
                                  }}
                                >
                                  <Remove sx={{ fontSize: 13 }} />
                                </IconButton>
                                <Typography sx={{ px: 0.75, fontWeight: 900, fontSize: "0.9rem", minWidth: 22, textAlign: "center", color: "#0f172a", letterSpacing: -0.5 }}>
                                  {item.quantity}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => onUpdateQuantity(index, item.quantity + 1)}
                                  sx={{
                                    width: 26, height: 26, p: 0,
                                    background: CART_BTN_GRADIENT,
                                    color: "#ffffff",
                                    borderRadius: "50%",
                                    boxShadow: CART_BTN_SHADOW,
                                    "&:hover": {
                                      background: CART_BTN_GRADIENT_HOVER,
                                      boxShadow: CART_BTN_SHADOW_HOVER,
                                    },
                                  }}
                                >
                                  <Add sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Box>

                              {/* Price */}
                              <Box sx={{ textAlign: "right" }}>
                                <Typography sx={{ fontWeight: 900, fontSize: "1.05rem", color: CART_ACCENT, lineHeight: 1, letterSpacing: -0.3 }}>
                                  {formatCurrency((unitPrice || 0) * item.quantity, activeCurrency)}
                                </Typography>
                                {item.quantity > 1 && (
                                  <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", mt: 0.25 }}>
                                    {formatCurrency(unitPrice || 0, activeCurrency)} × {item.quantity}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Box>

            {/* ── RIGHT PANEL: Details + Bill ── */}
            <Box
              sx={{
                flex: { xs: "0 0 auto", md: "0 0 45%" },
                display: "flex",
                flexDirection: "column",
                bgcolor: "#ffffff",
                borderTop: {
                  xs: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
                  md: "none",
                },
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Stack spacing={2.5}>
                  {/* Your Details */}
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 1,
                          bgcolor: alpha(CART_ACCENT, 0.1),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PersonOutlined
                          sx={{
                            fontSize: 15,
                            color: CART_ACCENT,
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.text.secondary,
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                        }}
                      >
                        Your Details
                      </Typography>
                    </Box>
                    <Stack spacing={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Contact Number"
                        value={customerMobile}
                        onChange={(e) =>
                          setCustomerMobile(
                            sanitizePhoneNumber(e.target.value).slice(0, 15),
                          )
                        }
                        placeholder="+1234567890"
                        required
                        error={isCustomerMobileInvalid}
                        helperText={
                          isCustomerMobileInvalid
                            ? "Enter a valid contact number (10-15 digits)"
                            : " "
                        }
                        inputProps={{ inputMode: "numeric", maxLength: 15 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneOutlined
                                sx={{
                                  fontSize: 17,
                                  color: theme.palette.text.secondary,
                                }}
                              />
                            </InputAdornment>
                          ),
                          sx: { borderRadius: 2 },
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Your Name (Optional)"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Enter your name"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonOutlined
                                sx={{
                                  fontSize: 17,
                                  color: theme.palette.text.secondary,
                                }}
                              />
                            </InputAdornment>
                          ),
                          sx: { borderRadius: 2 },
                        }}
                      />
                    </Stack>
                  </Box>

                  {/* Extras — Notes only */}
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 1,
                          bgcolor: alpha(CART_ACCENT, 0.08),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <NoteAltOutlined
                          sx={{ fontSize: 15, color: CART_ACCENT }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.text.secondary,
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                        }}
                      >
                        Notes
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      label="Additional Notes"
                      value={specialInstructions}
                      onChange={(e) => setSpecialInstructions(e.target.value)}
                      placeholder={
                        guestMode === "room-service"
                          ? "Allergies, extra cutlery, when to knock…"
                          : "Dietary preferences, allergies, special requests…"
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            sx={{ alignSelf: "flex-start", mt: 1.25 }}
                          >
                            <NoteAltOutlined
                              sx={{
                                fontSize: 16,
                                color: theme.palette.text.secondary,
                              }}
                            />
                          </InputAdornment>
                        ),
                        sx: { borderRadius: 2 },
                      }}
                    />
                  </Box>

                  {/* Discounts */}
                  {(availableDiscounts.length > 0 || !appliedDiscount) && (
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: 1,
                            bgcolor: alpha("#f59e0b", 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <LocalOfferOutlined
                            sx={{ fontSize: 15, color: "#f59e0b" }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.text.secondary,
                            textTransform: "uppercase",
                            letterSpacing: 0.8,
                          }}
                        >
                          Discounts
                        </Typography>
                      </Box>

                      <Stack spacing={1}>
                        {/* Discount cards */}
                        {availableDiscounts.map((d) => {
                          const isApplied = appliedDiscount?.id === d.id;
                          const min = parseFloat(d.minOrderAmount) || 0;
                          const maxSave = parseFloat(d.maxDiscountAmount) || 0;
                          const eligible = subtotal >= min;
                          const discountLabel =
                            d.type === "percentage"
                              ? `${parseFloat(d.value).toFixed(0)}% OFF`
                              : `${formatCurrency(d.value, activeCurrency)} OFF`;
                          return (
                            <Box
                              key={d.id}
                              onClick={() => {
                                if (!eligible) return;
                                isApplied
                                  ? handleRemoveDiscount()
                                  : handleApplyDiscount(d);
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "stretch",
                                borderRadius: 3,
                                overflow: "hidden",
                                border: `1.5px solid ${isApplied
                                  ? "#10b981"
                                  : eligible
                                    ? alpha(theme.palette.divider, 0.22)
                                    : alpha(theme.palette.divider, 0.15)
                                  }`,
                                boxShadow: isApplied
                                  ? `0 4px 16px ${alpha("#10b981", 0.18)}`
                                  : eligible
                                    ? `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`
                                    : "none",
                                cursor: eligible ? "pointer" : "default",
                                opacity: eligible ? 1 : 0.45,
                                transition: "all 0.2s ease",
                                bgcolor: isApplied
                                  ? alpha("#10b981", 0.04)
                                  : "#fff",
                                "&:hover": eligible
                                  ? {
                                    borderColor: isApplied
                                      ? "#059669"
                                      : CART_ACCENT,
                                    boxShadow: `0 4px 18px ${alpha(CART_ACCENT, 0.15)}`,
                                    transform: "translateY(-1px)",
                                  }
                                  : {},
                              }}
                            >
                              {/* Left badge — gradient pill showing the value */}
                              <Box
                                sx={{
                                  minWidth: 72,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  px: 1.25,
                                  py: 1.5,
                                  background: isApplied
                                    ? "linear-gradient(160deg, #10b981, #059669)"
                                    : eligible
                                      ? (luxury ? LUXURY.barGradient : "linear-gradient(160deg, #6b7dcf, #9b72cf)")
                                      : alpha(theme.palette.divider, 0.12),
                                  flexShrink: 0,
                                }}
                              >
                                <LocalOfferOutlined
                                  sx={{
                                    fontSize: 16,
                                    color: eligible ? "#fff" : "text.disabled",
                                    mb: 0.4,
                                    opacity: 0.85,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontWeight: 900,
                                    fontSize: "0.78rem",
                                    lineHeight: 1.15,
                                    color: eligible ? "#fff" : "text.disabled",
                                    textAlign: "center",
                                    letterSpacing: 0.2,
                                  }}
                                >
                                  {discountLabel
                                    .split(" ")
                                    .map((part, i) => (
                                      <span
                                        key={i}
                                        style={{ display: "block" }}
                                      >
                                        {part}
                                      </span>
                                    ))}
                                </Typography>
                              </Box>

                              {/* Right content */}
                              <Box
                                sx={{
                                  flex: 1,
                                  px: 1.5,
                                  py: 1.25,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  gap: 0.4,
                                }}
                              >
                                {/* Name + code pill */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 700,
                                      color: isApplied
                                        ? "#059669"
                                        : "text.primary",
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {d.name}
                                  </Typography>
                                  {isApplied ? (
                                    <CheckCircle
                                      sx={{ fontSize: 18, color: "#10b981", flexShrink: 0 }}
                                    />
                                  ) : (
                                    <Box
                                      sx={{
                                        px: 0.9,
                                        py: 0.15,
                                        borderRadius: "6px",
                                        bgcolor: alpha(CART_ACCENT, 0.09),
                                        border: `1px solid ${alpha(CART_ACCENT, 0.22)}`,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 800,
                                          fontSize: "0.66rem",
                                          letterSpacing: 1,
                                          color: CART_ACCENT,
                                        }}
                                      >
                                        {d.code}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>

                                {/* Meta pills */}
                                <Box
                                  sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}
                                >
                                  {min > 0 && (
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.3,
                                        px: 0.75,
                                        py: 0.2,
                                        borderRadius: "6px",
                                        bgcolor: eligible
                                          ? alpha(theme.palette.success.main, 0.07)
                                          : alpha(theme.palette.error.main, 0.08),
                                        border: `1px solid ${eligible
                                          ? alpha(theme.palette.success.main, 0.2)
                                          : alpha(theme.palette.error.main, 0.2)
                                          }`,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontSize: "0.65rem",
                                          fontWeight: 600,
                                          color: eligible
                                            ? theme.palette.success.dark
                                            : theme.palette.error.main,
                                        }}
                                      >
                                        {!eligible && "⚠ "}Min.{" "}
                                        {formatCurrency(min, activeCurrency)}
                                      </Typography>
                                    </Box>
                                  )}
                                  {maxSave > 0 && (
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        px: 0.75,
                                        py: 0.2,
                                        borderRadius: "6px",
                                        bgcolor: alpha("#f59e0b", 0.07),
                                        border: `1px solid ${alpha("#f59e0b", 0.2)}`,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontSize: "0.65rem",
                                          fontWeight: 600,
                                          color: "#b45309",
                                        }}
                                      >
                                        Save up to {formatCurrency(maxSave, activeCurrency)}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}

                        {/* Error message */}
                        {discountError && (
                          <Typography
                            variant="caption"
                            sx={{ color: "error.main", display: "block" }}
                          >
                            {discountError}
                          </Typography>
                        )}

                        {/* Promo code fallback */}
                        {availableDiscounts.length === 0 && (
                          <TextField
                            fullWidth
                            size="small"
                            label="Promo Code"
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value)}
                            placeholder="Have a promo code?"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocalOfferOutlined
                                    sx={{
                                      fontSize: 16,
                                      color: theme.palette.text.secondary,
                                    }}
                                  />
                                </InputAdornment>
                              ),
                              sx: { borderRadius: 2 },
                            }}
                          />
                        )}
                      </Stack>
                    </Box>
                  )}

                  {/* Bill Summary Card */}
                  <Box
                    sx={{
                      borderRadius: 3,
                      background: "linear-gradient(145deg, #ffffff 0%, #f5f6fa 100%)",
                      border: "1px solid #e8eaf5",
                      overflow: "hidden",
                      boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.06)}`,
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.25,
                        background: `linear-gradient(135deg, ${alpha(CART_ACCENT, 0.1)} 0%, ${alpha(CART_ACCENT, 0.04)} 100%)`,
                        borderBottom: `1px solid ${alpha(CART_ACCENT, 0.12)}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: CART_ACCENT }} />
                      <Typography sx={{ fontWeight: 700, fontSize: "0.72rem", color: CART_ACCENT, textTransform: "uppercase", letterSpacing: 1 }}>
                        Bill Summary
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={1}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Subtotal
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(subtotal, activeCurrency)}
                          </Typography>
                        </Box>
                        {serviceCharge > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Service Charge ({serviceChargeRate}%)
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {formatCurrency(serviceCharge, activeCurrency)}
                            </Typography>
                          </Box>
                        )}
                        {vatAmount > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              VAT ({activeVatRate}%)
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {formatCurrency(vatAmount, activeCurrency)}
                            </Typography>
                          </Box>
                        )}
                        {appliedDiscount && discountAmount > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Typography variant="body2" sx={{ color: "#059669" }}>
                                Discount ({appliedDiscount.code})
                              </Typography>
                              <CancelIcon
                                sx={{
                                  fontSize: 14,
                                  color: "#dc2626",
                                  cursor: "pointer",
                                  "&:hover": { opacity: 0.7 },
                                }}
                                onClick={handleRemoveDiscount}
                              />
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "#059669" }}
                            >
                              -{formatCurrency(discountAmount, activeCurrency)}
                            </Typography>
                          </Box>
                        )}
                        <Divider sx={{ my: 0.5 }} />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            Total
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 800,
                              color: CART_ACCENT,
                              fontSize: "1.15rem",
                            }}
                          >
                            {formatCurrency(grandTotal, activeCurrency)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>
            </Box>

              {/* Sticky Place Order CTA */}
              <Box
                sx={{
                  px: 2.5,
                  pt: 2,
                  pb: guestMode
                    ? "max(28px, calc(env(safe-area-inset-bottom) + 20px))"
                    : "max(24px, calc(env(safe-area-inset-bottom) + 16px))",
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                  bgcolor: "#ffffff",
                  flexShrink: 0,
                  boxShadow: `0 -4px 16px ${alpha(theme.palette.common.black, 0.05)}`,
                }}
              >
                {orderType === "dine-in" && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
                      Payment Timing
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {[{ val: "now", label: "Pay Now" }, { val: "later", label: "Pay Later" }].map(({ val, label }) => (
                        <Button
                          key={val}
                          fullWidth
                          size="small"
                          variant={dineInPayChoice === val ? "contained" : "outlined"}
                          onClick={() => setDineInPayChoice(val)}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 700,
                            py: 0.9,
                            borderColor: dineInPayChoice === val ? "transparent" : "#d4d9f5",
                            color: dineInPayChoice === val ? "#ffffff" : "#5d6bb3",
                            background: dineInPayChoice === val
                              ? CART_BTN_GRADIENT
                              : "#ffffff",
                            boxShadow: dineInPayChoice === val ? CART_BTN_SHADOW : "none",
                            "&:hover": {
                              borderColor: dineInPayChoice === val ? "transparent" : "#c6cdf1",
                              background: dineInPayChoice === val
                                ? CART_BTN_GRADIENT_HOVER
                                : "#f8f9fe",
                              boxShadow: dineInPayChoice === val ? CART_BTN_SHADOW_HOVER : "none",
                            },
                          }}
                        >
                          {label}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}
                {/* Grand total preview */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, px: 0.5 }}>
                  <Typography sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.88rem" }}>Grand Total</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: "1.3rem", color: CART_ACCENT, lineHeight: 1 }}>
                    {formatCurrency(grandTotal, activeCurrency)}
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handlePlaceOrder}
                  disabled={loading || ORDER_TYPES.length === 0 || !orderType || !detailsReady}
                  sx={{
                    py: 1.6,
                    fontSize: "1rem",
                    fontWeight: 800,
                    borderRadius: 3,
                    textTransform: "none",
                    letterSpacing: 0.3,
                    background: detailsReady ? CART_BTN_GRADIENT : "#D6D3D1",
                    color: detailsReady ? "#ffffff" : "#78716C",
                    boxShadow: detailsReady && !loading ? CART_BTN_SHADOW : "none",
                    cursor: detailsReady ? "pointer" : "not-allowed",
                    "&:hover": {
                      background: detailsReady ? CART_BTN_GRADIENT_HOVER : "#D6D3D1",
                      boxShadow: detailsReady && !loading ? CART_BTN_SHADOW_HOVER : "none",
                      transform: detailsReady && !loading ? "translateY(-1px)" : "none",
                    },
                    "&.Mui-disabled": {
                      background: "#D6D3D1",
                      color: "#78716C",
                      boxShadow: "none",
                      opacity: 1,
                    },
                    "&.Mui-disabled:hover": {
                      background: "#D6D3D1",
                      color: "#78716C",
                      boxShadow: "none",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  {loading ? "Processing your order…" : "Place Order"}
                </Button>
                {!detailsReady && !loading && (
                  <Typography sx={{ mt: 1, fontSize: "0.72rem", color: "#94a3b8", fontWeight: 600, textAlign: "center" }}>
                    Enter a valid contact number to place the order.
                  </Typography>
                )}
              </Box>
          </Box>
        )}
      </Drawer>

      <OrderSuccessDialog
        luxury={luxury}
        open={showSuccessDialog}
        onClose={handleSuccessClose}
        orderResult={orderResult}
        companyInfo={companyData}
        companyId={companyId}
      />
    </>
  );
};

export default CartDrawer;
